import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Link2, PencilLine, Library as LibraryIcon, ArrowLeft, Sparkles, Search, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, money, productImage } from "@/lib/api";
import { Field, SectionTitle, Thumb, PageHeader, Pill } from "@/components/Bits";
import ProjectNav from "@/components/ProjectNav";
import NewVendorDialog from "@/components/NewVendorDialog";
import ProductImagesField from "@/components/ProductImagesField";

const input = "w-full rounded-sm border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-lime";
const SELECTION = ["Considering", "Shortlisted", "Presented", "Awaiting Approval", "Approved"];

const blankQuick = {
  product_url: "", name: "", primary_image: "", primary_image_file_id: "", image_file_ids: [], images: [], vendor_id: "", retail_price: "",
  category: "", quantity: 1, selection_status: "Considering", notes: "", sku: "",
  description: "", brand: "", dimensions: "", material: "", color_finish: "",
  availability: "", specifications: null,
};

const FIELD_LABELS = {
  name: "Product name", images: "Images", price: "Price", currency: "Currency",
  vendor: "Retailer", brand: "Brand", description: "Description", dimensions: "Dimensions",
  material: "Materials", color_finish: "Colour / finish", specifications: "Specifications",
  sku: "SKU", availability: "Availability",
};

export default function AddProduct() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState(params.get("mode") || null);
  const [projectId, setProjectId] = useState(params.get("project") || "");
  const [spaceId, setSpaceId] = useState(params.get("space") || "");
  const [projects, setProjects] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [quick, setQuick] = useState(blankQuick);
  const [fetching, setFetching] = useState(false);
  const [report, setReport] = useState(null);
  const [vendorDialog, setVendorDialog] = useState(false);
  const [busy, setBusy] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libQuery, setLibQuery] = useState("");
  const [libPick, setLibPick] = useState(null);
  const [libForm, setLibForm] = useState({ quantity: 1, selection_status: "Considering", retail_price: 0 });

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/meta/enums").then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (projectId) api.get(`/projects/${projectId}/spaces`).then((r) => setSpaces(r.data)).catch(() => setSpaces([]));
    else setSpaces([]);
  }, [projectId]);

  useEffect(() => {
    if (mode === "library") {
      api.get("/products", { params: { library: true, q: libQuery || undefined } })
        .then((r) => setLibrary(r.data)).catch(() => setLibrary([]));
    }
  }, [mode, libQuery]);

  const fetchMeta = async () => {
    const url = quick.product_url.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast.error("Paste a full product URL starting with https://");
      return;
    }
    setFetching(true);
    setReport(null);
    try {
      const { data } = await api.get("/products/metadata", { params: { url } });
      setReport(data);
      setQuick((q) => ({
        ...q,
        name: q.name || data.name || "",
        retail_price: q.retail_price || (data.price ?? ""),
        description: q.description || data.description || "",
        sku: q.sku || data.sku || "",
        brand: q.brand || data.brand || data.vendor || "",
        dimensions: q.dimensions || data.dimensions || "",
        material: q.material || data.material || "",
        color_finish: q.color_finish || data.color_finish || "",
        availability: q.availability || data.availability || "",
        specifications: q.specifications || data.specifications || null,
        vendor_id: q.vendor_id || data.vendor_match?.id || "",
      }));
      if (data.blocked) toast.message("Couldn’t read that retailer — details stay yours to enter");
      else if (data.fetched) toast.success(`Read ${data.found.length} details — review before saving`);
      else toast.message("That page published nothing we can read — enter details manually");
    } catch (e) {
      setReport({ blocked: true, message: apiError(e), found: [], missing: [] });
      toast.error(apiError(e));
    } finally {
      setFetching(false);
    }
  };

  const saveQuick = async (addAnother) => {
    setBusy(true);
    try {
      let vendorId = quick.vendor_id || null;
      // A new retailer becomes a vendor once, resolved server-side so nothing duplicates.
      if (!vendorId && report?.vendor_suggestion) {
        const { data: vendor } = await api.post("/vendors/resolve", {
          name: report.vendor_suggestion,
          website: report.url,
        });
        vendorId = vendor.id;
        if (vendor.created) setVendors((v) => [...v, vendor]);
      }
      const payload = {
        ...quick,
        retail_price: quick.retail_price === "" ? 0 : Number(quick.retail_price) || 0,
        quantity: Number(quick.quantity) || 1,
        category: quick.category || null,
        project_id: projectId || null,
        space_id: spaceId || null,
        vendor_id: vendorId,
        markup_type: "percentage",
        markup_percentage: 35,
      };
      const { data } = await api.post("/products", payload);
      if (addAnother) {
        setQuick({ ...blankQuick, category: quick.category });
        setReport(null);
        toast.success(`${data.name} saved — add another`);
      } else {
        toast.success("Product saved");
        navigate(`/products/${data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addFromLibrary = async () => {
    if (!projectId) {
      toast.error("Choose a project first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/products/${libPick.id}/copy-to-project`, {
        project_id: projectId, space_id: spaceId || null,
      });
      await api.patch(`/products/${data.id}`, {
        quantity: Number(libForm.quantity) || 1,
        selection_status: libForm.selection_status,
        retail_price: Number(libForm.retail_price) || libPick.retail_price || 0,
      });
      toast.success("Added to project as a new selection");
      navigate(`/products/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ProjectSpacePickers = (
    <div className="grid gap-6 sm:grid-cols-2">
      <Field label="Project">
        <select data-testid="add-project-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} className={input}>
          <option value="">— None (library item) —</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="Space">
        <select data-testid="add-space-select" value={spaceId} onChange={(e) => setSpaceId(e.target.value)} className={input}>
          <option value="">— Unassigned —</option>
          {spaces.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
    </div>
  );

  if (!mode) {
    const options = [
      ["link", "Add From Link", "Paste a retailer or manufacturer URL and we’ll pull what we can.", Link2],
      ["manual", "Add Manually", "Full product form with financials and client visibility.", PencilLine],
      ["library", "Add From Library", "Reuse a specification you’ve already sourced.", LibraryIcon],
    ];
    return (
      <div className="space-y-10" data-testid="add-product-chooser">
        {projectId && (
          <ProjectNav
            projectId={projectId}
            projectName={projects.find((p) => p.id === projectId)?.name || "Project"}
            activeSection="Selections"
            trail={[
              { label: "Products / Sourcing", to: `/projects/${projectId}?tab=Selections` },
              { label: "Add Product" },
            ]}
          />
        )}
        <PageHeader overline="Sourcing" title="How would you like to add this product?" subtitle="Pick the fastest path for what you have in hand." />
        <div className="grid gap-6 md:grid-cols-3">
          {options.map(([key, title, body, Icon]) => (
            <button
              key={key}
              data-testid={`add-mode-${key}`}
              onClick={() => (key === "manual" ? navigate(`/products/manual?project=${projectId}&space=${spaceId}`) : setMode(key))}
              className="card-surface card-hover group p-7 text-left"
            >
              <Icon className="h-6 w-6 text-lime-dark" strokeWidth={1.5} />
              <h3 className="mt-5 font-serif text-2xl text-ink">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-stone">{body}</p>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10 pb-10" data-testid={`add-product-${mode}`}>
      <NewVendorDialog
        open={vendorDialog}
        existing={vendors}
        onClose={() => setVendorDialog(false)}
        onCreated={(vendor) => {
          setVendors((list) => (list.some((v) => v.id === vendor.id) ? list : [...list, vendor]));
          setQuick((q) => ({ ...q, vendor_id: vendor.id }));
        }}
      />
      {projectId && (
        <ProjectNav
          projectId={projectId}
          projectName={projects.find((p) => p.id === projectId)?.name || "Project"}
          activeSection="Selections"
          trail={[
            { label: "Products / Sourcing", to: `/projects/${projectId}?tab=Selections` },
            { label: "Add Product" },
          ]}
        />
      )}
      <button onClick={() => setMode(null)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.5} /> Change method
      </button>

      {mode === "link" && (
        <>
          <PageHeader overline="Rapid Sourcing" title="Add From Link" subtitle="Paste a product URL. We’ll try to read the page — anything missing you can type in." />
          <section className="card-surface space-y-7 p-7">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <Field label="Paste Product URL" className="flex-1">
                <input data-testid="quick-url-input" value={quick.product_url} onChange={(e) => setQuick({ ...quick, product_url: e.target.value })} placeholder="https://…" className={input} />
              </Field>
              <button data-testid="fetch-metadata-btn" onClick={fetchMeta} disabled={fetching} className="inline-flex items-center gap-2 rounded-sm border border-line bg-white px-4 py-2.5 text-sm text-ink hover:border-lime disabled:opacity-60">
                <Sparkles className="h-4 w-4" strokeWidth={1.5} /> {fetching ? "Reading…" : "Fetch Details"}
              </button>
            </div>

            {report && (
              <div
                data-testid="extraction-report"
                className={`rounded-sm border px-5 py-4 text-sm ${
                  report.blocked ? "border-[#E07A5F] bg-[#F6DED6] text-[#A8492E]" : "border-line bg-bone"
                }`}
              >
                {report.message && <p className="leading-relaxed" data-testid="extraction-message">{report.message}</p>}
                {!!report.found?.length && (
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="overline mr-1">Retrieved</span>
                    {report.found.map((f) => (
                      <Pill key={f} testId={`found-${f}`} tone="bg-lime text-ink">{FIELD_LABELS[f] || f}</Pill>
                    ))}
                  </div>
                )}
                {!!report.missing?.length && !report.blocked && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <span className="overline mr-1">Needs review</span>
                    {report.missing.map((f) => (
                      <Pill key={f} testId={`missing-${f}`} tone="bg-line text-stone">{FIELD_LABELS[f] || f}</Pill>
                    ))}
                  </div>
                )}
                {report.vendor_suggestion && (
                  <p className="mt-3 text-xs text-stone" data-testid="vendor-note">
                    Retailer:{" "}
                    <strong className="text-ink">{report.vendor_match?.name || report.vendor_suggestion}</strong>
                    {report.vendor_match ? " — matched to an existing vendor." : " — will be created as a new vendor when you save."}
                  </p>
                )}
              </div>
            )}

            <div className="grid gap-7 lg:grid-cols-[240px_1fr]">
              <div>
                <ProductImagesField
                  projectId={projectId || null}
                  value={quick}
                  extraUrls={report?.images || []}
                  onChange={(patch) => setQuick((q) => ({ ...q, ...patch }))}
                />
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field label="Product Name" className="sm:col-span-2">
                  <input data-testid="quick-name-input" required value={quick.name} onChange={(e) => setQuick({ ...quick, name: e.target.value })} className={input} />
                </Field>
                <Field label="Vendor">
                  <div className="flex gap-2">
                    <select data-testid="quick-vendor-select" value={quick.vendor_id} onChange={(e) => setQuick({ ...quick, vendor_id: e.target.value })} className={input}>
                      <option value="">{report?.vendor_suggestion ? `— Create ${report.vendor_suggestion} —` : "— No vendor —"}</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                    <button
                      type="button"
                      data-testid="add-new-vendor-btn"
                      onClick={() => setVendorDialog(true)}
                      title="Add a new vendor"
                      className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-line bg-white px-3 py-2.5 text-sm text-ink transition-colors hover:border-lime"
                    >
                      <Store className="h-4 w-4" strokeWidth={1.5} />
                      <span className="hidden sm:inline">Add New</span>
                    </button>
                  </div>
                </Field>
                <Field label="Price" hint={report && !report.price ? "Not found — enter the retailer’s price" : undefined}>
                  <input data-testid="quick-price-input" type="number" step="0.01" placeholder="Not found" value={quick.retail_price} onChange={(e) => setQuick({ ...quick, retail_price: e.target.value })} className={`num ${input}`} />
                </Field>
                <Field label="Category">
                  <select data-testid="quick-category-select" value={quick.category} onChange={(e) => setQuick({ ...quick, category: e.target.value })} className={input}>
                    <option value="">— Needs review —</option>
                    {categories.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Quantity">
                  <input data-testid="quick-quantity-input" type="number" min="1" value={quick.quantity} onChange={(e) => setQuick({ ...quick, quantity: e.target.value })} className={`num ${input}`} />
                </Field>
                <Field label="Selection Status">
                  <select data-testid="quick-status-select" value={quick.selection_status} onChange={(e) => setQuick({ ...quick, selection_status: e.target.value })} className={input}>
                    {SELECTION.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="SKU">
                  <input data-testid="quick-sku-input" placeholder="Not found" value={quick.sku} onChange={(e) => setQuick({ ...quick, sku: e.target.value })} className={input} />
                </Field>
                <Field label="Availability">
                  <input data-testid="quick-availability-input" placeholder="Not found" value={quick.availability} onChange={(e) => setQuick({ ...quick, availability: e.target.value })} className={input} />
                </Field>
                <Field label="Dimensions">
                  <input data-testid="quick-dimensions-input" placeholder="Not found" value={quick.dimensions} onChange={(e) => setQuick({ ...quick, dimensions: e.target.value })} className={input} />
                </Field>
                <Field label="Materials">
                  <input data-testid="quick-material-input" placeholder="Not found" value={quick.material} onChange={(e) => setQuick({ ...quick, material: e.target.value })} className={input} />
                </Field>
                <Field label="Colour / Finish">
                  <input data-testid="quick-color-input" placeholder="Not found" value={quick.color_finish} onChange={(e) => setQuick({ ...quick, color_finish: e.target.value })} className={input} />
                </Field>
                <Field label="Brand">
                  <input data-testid="quick-brand-input" placeholder="Not found" value={quick.brand} onChange={(e) => setQuick({ ...quick, brand: e.target.value })} className={input} />
                </Field>
                <Field label="Description" className="sm:col-span-2">
                  <textarea data-testid="quick-description-input" rows={3} placeholder="Not found" value={quick.description} onChange={(e) => setQuick({ ...quick, description: e.target.value })} className={input} />
                </Field>
                {quick.specifications && (
                  <div className="sm:col-span-2" data-testid="quick-specifications">
                    <span className="overline mb-2 block">Specifications from the retailer</span>
                    <dl className="grid gap-x-6 gap-y-2 rounded-sm border border-line bg-bone p-4 text-sm sm:grid-cols-2">
                      {Object.entries(quick.specifications).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <dt className="text-stone">{k}:</dt>
                          <dd className="text-ink">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
                <Field label="Notes" className="sm:col-span-2">
                  <textarea data-testid="quick-notes-input" rows={2} value={quick.notes} onChange={(e) => setQuick({ ...quick, notes: e.target.value })} className={input} />
                </Field>
              </div>
            </div>

            {ProjectSpacePickers}

            <div className="flex flex-wrap gap-3 border-t border-line pt-6">
              <button data-testid="quick-save-btn" onClick={() => saveQuick(false)} disabled={busy || !quick.name} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
                Save Product
              </button>
              <button data-testid="quick-save-another-btn" onClick={() => saveQuick(true)} disabled={busy || !quick.name} className="rounded-sm border border-line bg-white px-5 py-2.5 text-sm text-ink hover:border-lime disabled:opacity-60">
                Save &amp; Add Another
              </button>
              <span className="self-center text-xs text-fog">Project and space stay selected so you can source a room quickly.</span>
            </div>
          </section>
        </>
      )}

      {mode === "library" && (
        <>
          <PageHeader overline="Sourcing" title="Add From Library" subtitle="Search your reusable specifications and drop one into a project." />
          <section className="card-surface space-y-6 p-7">
            <div className="relative sm:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fog" strokeWidth={1.5} />
              <input data-testid="library-picker-search" value={libQuery} onChange={(e) => setLibQuery(e.target.value)} placeholder="Search name, SKU or brand" className="w-full rounded-sm border border-line bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-lime" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2" data-testid="library-picker-list">
              {library.map((p) => (
                <button
                  key={p.id}
                  data-testid={`library-pick-${p.id}`}
                  onClick={() => {
                    setLibPick(p);
                    setLibForm({ quantity: 1, selection_status: "Considering", retail_price: p.retail_price || 0 });
                  }}
                  className={`flex items-center gap-4 rounded-sm border p-3 text-left transition-colors ${
                    libPick?.id === p.id ? "border-lime bg-[#EDEFC4]/50" : "border-line hover:border-fog"
                  }`}
                >
                  <Thumb src={productImage(p)} className="h-14 w-16" />
                  <div className="min-w-0">
                    <div className="truncate text-sm text-ink">{p.name}</div>
                    <div className="num text-xs text-fog">{money(p.retail_price)} · {p.category}</div>
                    {!!p.used_in_projects?.length && <div className="mt-1 text-[11px] text-fog">Used in {p.used_in_projects.join(", ")}</div>}
                  </div>
                </button>
              ))}
              {!library.length && <p className="text-sm text-fog">No library products match.</p>}
            </div>

            {libPick && (
              <div className="space-y-6 border-t border-line pt-6" data-testid="library-add-form">
                <SectionTitle>Add “{libPick.name}” to a project</SectionTitle>
                {ProjectSpacePickers}
                <div className="grid gap-6 sm:grid-cols-3">
                  <Field label="Quantity">
                    <input data-testid="library-quantity-input" type="number" min="1" value={libForm.quantity} onChange={(e) => setLibForm({ ...libForm, quantity: e.target.value })} className={`num ${input}`} />
                  </Field>
                  <Field label="Current Price">
                    <input data-testid="library-price-input" type="number" step="0.01" value={libForm.retail_price} onChange={(e) => setLibForm({ ...libForm, retail_price: e.target.value })} className={`num ${input}`} />
                  </Field>
                  <Field label="Selection Status">
                    <select data-testid="library-status-select" value={libForm.selection_status} onChange={(e) => setLibForm({ ...libForm, selection_status: e.target.value })} className={input}>
                      {SELECTION.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                </div>
                <button data-testid="library-add-submit" onClick={addFromLibrary} disabled={busy} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark disabled:opacity-60">
                  {busy ? "Adding…" : "Add to Project"}
                </button>
                <p className="text-[11px] leading-relaxed text-fog">
                  This creates a new project selection. Later price changes to the library master never alter this
                  project’s historical record.
                </p>
              </div>
            )}
          </section>
        </>
      )}

      <Link to="/products/manual" className="inline-block text-xs uppercase tracking-[0.15em] text-stone hover:text-ink">
        Prefer the full form? Add manually →
      </Link>
    </div>
  );
}
